import React from 'react';
import { X, Copy, Search, Loader, GitMerge } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

/**
 * "Find Duplicate Clinics" modal for the Surgeons Database page.
 *
 * Lets staff cluster clinic records two ways and merge each pair:
 *  - 'postcode': same postcode (server-side grouping).
 *  - 'geo': within `dupeRadius` metres of each other (GPS coords came
 *    from the KMZ/ezyVet imports, so this catches dupes whose text
 *    differs).
 * onSearch triggers the backend query (GET /api/surgeons-db/clinics/
 * duplicates); results arrive as `dupeGroups`, an array of arrays — each
 * inner array is one cluster of suspected-dupe clinic records.
 * onMerge(keep, discard) hands a chosen pair up to the page, which opens
 * MergeClinicModal. The 'ezyvet'/'simons' tags show which import source
 * produced each record — most dupes are one of each.
 *
 * Presentational only: all state and API calls live in SurgeonsDatabase,
 * which mounts it conditionally (`{showDupeModal && …}`), so the Radix
 * Dialog is simply `open` — Escape and overlay-click both route to
 * onClose, keeping parent state consistent.
 */

const tagClass = (tag) => {
  if (tag === 'ezyvet') return 'text-xs px-1.5 rounded-full font-medium border bg-blue-50 text-blue-600 border-blue-200';
  if (tag === 'simons') return 'text-xs px-1.5 rounded-full font-medium border bg-green-50 text-green-600 border-green-200';
  return 'text-xs px-1.5 rounded-full font-medium border bg-gray-50 text-gray-500 border-gray-200';
};
const tagLabel = (tag) => {
  if (tag === 'ezyvet') return 'EzyVet';
  if (tag === 'simons') return "Simon's";
  return tag;
};

const DuplicateFinderModal = ({
  dupeMode, setDupeMode,
  dupeRadius, setDupeRadius,
  dupeGroups, dupeLoading,
  onSearch, onClose, onMerge,
}) => (
  <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent className="max-w-3xl flex flex-col overflow-y-auto" style={{maxHeight:'90vh'}}>
      <div className="flex justify-between items-center p-6 border-b border-gray-200 shrink-0">
        <div className="flex items-center space-x-2">
          <Copy className="h-5 w-5 text-amber-600" />
          <DialogTitle>Find Duplicate Clinics</DialogTitle>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      <div className="p-4 border-b border-gray-100 flex items-center space-x-4 shrink-0">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">Match by:</span>
          {[{v:'postcode',l:'Postcode'},{v:'geo',l:'GPS proximity'}].map(function(item) {
            return (
              <button key={item.v} onClick={() => setDupeMode(item.v)}
                className={dupeMode === item.v
                  ? 'px-3 py-1 rounded-full text-xs font-medium border bg-amber-500 text-white border-amber-500'
                  : 'px-3 py-1 rounded-full text-xs font-medium border bg-white text-gray-600 border-gray-300 hover:border-amber-400'}>
                {item.l}
              </button>
            );
          })}
        </div>
        {dupeMode === 'geo' && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Radius:</span>
            <input type="number" value={dupeRadius} onChange={e => setDupeRadius(Number(e.target.value))}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm" min={10} max={5000} step={10} />
            <span className="text-sm text-gray-400">m</span>
          </div>
        )}
        <button onClick={onSearch} disabled={dupeLoading}
          className="ml-auto px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium flex items-center space-x-1 disabled:opacity-50">
          {dupeLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          <span>{dupeLoading ? 'Searching...' : 'Search'}</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!dupeLoading && dupeGroups.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Copy className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Click Search to find potential duplicates</p>
          </div>
        )}
        {dupeGroups.map(function(group, gi) {
          return (
            <div key={gi} className="border border-amber-200 rounded-xl overflow-hidden">
              <div className="bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-700 uppercase tracking-wide">
                Group {gi + 1} — {group.length} possible duplicates
              </div>
              <div className="divide-y divide-gray-100">
                {group.map(function(clinic, ci) {
                  return (
                    <div key={clinic.id} className="px-4 py-3 flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900">{clinic.name}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{clinic.address}</div>
                        <div className="flex items-center gap-1 mt-1">
                          <span className="text-xs text-gray-400">{clinic.id}</span>
                          {(clinic.tags || []).map(function(tag) {
                            return <span key={tag} className={tagClass(tag)}>{tagLabel(tag)}</span>;
                          })}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 ml-3 shrink-0">
                        {group.filter(function(_, j) { return j !== ci; }).map(function(other) {
                          return (
                            <button key={other.id} onClick={() => onMerge(clinic, other)}
                              className="flex items-center space-x-1 px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg text-xs font-medium">
                              <GitMerge className="h-3 w-3" /><span>Keep this, merge {other.id}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {!dupeLoading && dupeGroups.length > 0 && (
          <p className="text-center text-xs text-gray-400">{dupeGroups.length} group{dupeGroups.length !== 1 ? 's' : ''} found</p>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

export default DuplicateFinderModal;
